import { Pencil, Plus, Trash2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { formatCurrency } from "@/lib/formatters";
import { cn } from "@/lib/utils";

import type { Book, BudgetRule, BudgetRuleKind } from "@/schemas";

import type { GroupedCategory } from "../types";

import { EmptyStateCard } from "./empty-state-card";

export function RuleGroupsPanel({
  kind,
  groups,
  currencyCode,
  fiatById,
  booksById,
  onCreate,
  onCreateInCategory,
  onEdit,
  onDelete,
}: {
  kind: BudgetRuleKind;
  groups: GroupedCategory[];
  currencyCode: string;
  fiatById: Map<string, string>;
  booksById: Map<string, Book>;
  onCreate: () => void;
  onCreateInCategory: (category: string) => void;
  onEdit: (rule: BudgetRule) => void;
  onDelete: (ruleId: string) => void;
}) {
  return (
    <div className="space-y-6">
      {groups.length > 0 ? (
        groups.map((group) => (
          <div key={`${kind}-${group.name}`} className="space-y-3">
            <div className="flex items-center justify-between border-b pb-2">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold tracking-tight">
                  {group.name}
                </h3>
                <Badge variant="secondary" className="font-normal">
                  {formatCurrency(group.total, currencyCode)}
                </Badge>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onCreateInCategory(group.name)}
              >
                <Plus className="mr-2 h-4 w-4" />
                Add
              </Button>
            </div>

            <div className="grid gap-2">
              {group.rules.map((rule) => (
                <div
                  key={rule.id}
                  className="group flex items-center justify-between rounded-lg border bg-card p-4 transition-colors hover:bg-accent/50"
                >
                  <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-4">
                    <div className="flex items-center gap-2">
                      <span
                        className={cn(
                          "font-medium",
                          !rule.enabled && "text-muted-foreground line-through",
                        )}
                      >
                        {rule.label || "Untitled item"}
                      </span>
                      {!rule.enabled && (
                        <Badge
                          variant="outline"
                          className="text-[10px] uppercase"
                        >
                          Off
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span>Day {rule.day_of_month || 1}</span>
                      {rule.kind === "investment" && rule.linked_book_id && (
                        <>
                          <span>•</span>
                          <span>
                            {booksById.get(rule.linked_book_id)?.name ||
                              "Portfolio"}
                          </span>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <span className="font-medium">
                        {formatCurrency(rule.amount, currencyCode)}
                      </span>
                      <span className="ml-1 text-xs text-muted-foreground uppercase">
                        {fiatById.get(rule.currency_asset_id) ||
                          rule.currency_asset_id}
                      </span>
                    </div>

                    <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => onEdit(rule)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => void onDelete(rule.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))
      ) : (
        <EmptyStateCard text={`No ${kind} items found.`} />
      )}

      <Button variant="outline" className="w-full" onClick={onCreate}>
        <Plus className="mr-2 h-4 w-4" />
        Add new {kind} item
      </Button>
    </div>
  );
}
